<script setup>
import { Card, Label, Input, Button, Icon, Modal, MediaUploaderButton, Tabs, Tab, Textarea, ProfilePicture } from '@components'
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { getPrice } from '@utils'
import moment from 'moment'

// Route.
import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()

// Customer
import { useCustomer } from '@store/customer.js'
const $customer = useCustomer()

// Appointment
import { useAppointment } from '@store/appointment'
const $appointment = useAppointment()

// Service.
import { useService } from '@store/service'
const $service = useService()

// Staff.
import { useStaff } from '@store/staff'
const $staff = useStaff()

// Transaction.
import { useTransaction } from '@store/transaction'
const $transaction = useTransaction()

import { useSettings } from '@store/settings'
const $settings = useSettings()

// State.
const state = reactive({})

const customer = computed(() => {
    return $customer.getCustomer || {}
})

onUnmounted(() => {
    $customer.resetCustomer()
})

onMounted(async () => {
    state.loading = true
    await $appointment.fetchData()
    await $staff.fetchData()
    await $service.fetchData()
    await $customer.fetchCustomer(route.params.customerId)
    await $transaction.fetchTransactions()
    state.loading = false
    Log('CUSTOMER', customer.value)
})

const showNote = ref(false)

const noteText = ref('')
const submitNote = async () => {
    if (noteText.value == '') {
        return Toast.error('Please enter note text')
    }
    state.loading = true

    customer.value.notes.unshift({
        text: noteText.value,
        admin_id: prebook_admin.profile.id || 1,
        admin_name: prebook_admin.profile.display_name || 'Admin',
        added_at: new Date().toLocaleString()
    })

    showNote.value = false

    const updated = await customer.value.update({
        notes: customer.value.notes
    }, customer.value.customer_id)

    Log('UPDATED', updated)

    state.loading = false

    Toast.success('Note added successfully')

    noteText.value = ''

}

const deleteCustomer = async () => {
    if (!customer.value.customer_id) return Toast.error(`No ${String($settings.labels.customer?.singular).toLowerCase()} id found`)

    const response = await customer.value.delete()

    if (response.success) {
        Toast.success(`${$settings.labels.customer?.singular} deleted successfully`)
        router.push({ name: 'customer.list' })
    }
}

const additionalInfos = computed(() => {
    let totalSpent = 0
    let customerAppointments = $appointment.getAppointments?.filter(app => app.customer_id == customer.value.customer_id) || []
    let totalAppointment = customer.value.appointments?.length || 0
    let approved = [...customerAppointments?.filter(app => app.meta?.status == 'approved')] || []
    let canceled = [...customerAppointments?.filter(app => app.meta?.status == 'canceled')] || []
    let upcoming = [...customerAppointments?.filter(app => moment(app.booking_date, 'YYYY-MM-DD').isAfter(new Date()) && (app.meta?.status == 'approved' || app.meta?.status == 'pending'))] || []
    let past = [...customerAppointments?.filter(app => moment(app.booking_date, 'YYYY-MM-DD').isBefore(new Date()) && (app.meta?.status == 'approved' || app.meta?.status == 'pending'))] || []
    // let transactionsByCustomer = $transaction.getTransactions.filter(trans => trans.customer_id == customer.value.customer_id)
    let bookingServices = []

    customerAppointments.forEach(app => {
        if (bookingServices.find(s => s.service_id == app.service_id)) return
        let service = $service.getServices?.find(s => s.service_id == app.service_id) || []

        bookingServices.push({
            service_id: service.service_id,
            service_name: service.name,
            color: service.meta?.color,
            totalBooked: customerAppointments?.filter(app => app.service_id == service.service_id).length
        })
    })

    approved.forEach(app => {
        let transactionByAppointment = $transaction.getTransactions?.filter(trans => trans.appointment_id == app.appointment_id && trans.payment_status == 'paid') || []

        transactionByAppointment.forEach(trans => {
            totalSpent += Number(trans.payment_amount)
        })
    })
    return { totalAppointment, totalSpent, approved, canceled, upcoming, past, bookingServices }
})

const getStatusColors = (status) => {
    if (status == 'approved') return { color: '#027A48', bg: '#ECFDF3' }
    else if (status == 'canceled') return { color: '#FEE2E2', bg: '#DC2626' }
    else if (status == 'pending') return { color: '#F3F4F6', bg: '#64748B' }
    else return { color: '#F3F4F6', bg: '#475569' }
}
const backButton = () => {
    if (route.query.backTo) {
        router.push(route.query.backTo)
    } else {
        router.push({ name: 'customer.list' })
    }
}

// const findStaffById = (staff_id) => {
//     return $staff.getStaffs?.find(staff => staff.staff_id == staff_id) || {}
// }

const formatCustomerNoteTime = (dateTime) => {
    const formattedDate = moment(dateTime, 'M/D/YYYY, h:mm:ss A')
    if (formattedDate.isValid()) {
        return formattedDate.format('MMM D, YYYY')
    } else {
        return dateTime
    }
}
</script>

<template>
    <Modal :loading="state.loading">

        <!-- header  -->
        <template #header>
            <div class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between shadow-[0px_3px_0px_0px_#eaecf0]">
                <div @click="backButton" class="cursor-pointer flex items-center gap-5 text-[#101828] text-lg font-semibold">
                    <Icon name="arrowNarrowLeft" size="24px"></Icon> Back
                </div>
            </div>
        </template>

        <template #default>
            <div class="my-[-28px] md:w-[736px] m-auto" :class="{ 'cursor-wait': state.loading }">
                <!-- profile section -->
                <div class="flex flex-col gap-6 md:flex-row mb-7">
                    <!-- profile image -->
                    <div class="relative w-[160px] h-[160px]">
                        <!-- <div v-if="customer.meta?.profile_image_url" class="overflow-hidden rounded-full border-4 border-[#fff] w-[160px] h-[160px] shadow-md">
                            <img :src="customer.meta?.profile_image_url" class="w-full h-full">
                        </div>
                        <div v-else class="bg-[#9E77ED] text-white rounded-full border-4 border-[#fff] w-[160px] h-[160px] flex items-center justify-center uppercase font-sans text-5xl font-bold shadow-md">
                            {{ customer.name?.split('')[0] }}
                        </div> -->
                        <ProfilePicture :name="customer.name" :img="customer.meta?.profile_image_url" :bgColor="customer.meta?.color" class="w-[160px] h-[160px]" imgClass="border-4 border-[#fff] shadow-md" textClass="border-4 border-[#fff] shadow-md !text-5xl" />
                        <div v-if="customer.meta?.priority" class="bg-[#9E77ED] cursor-pointer absolute bottom-0 right-0 border-[4px] border-[#fff] text-[#fff] w-11 h-11 rounded-full flex items-center justify-center">
                            <Icon name="star01" size="24px" class="fill-white" />
                        </div>
                    </div>

                    <!-- profile details -->
                    <div class="w-full flex flex-col justify-center">
                        <h1 v-if="customer.name" class="text-[#101828] text-2xl font-semibold mb-1">{{ customer.name }}</h1>
                        <p v-if="customer.ucid" class="text-[#98A2B3] text-base font-medium mb-2">{{ customer.ucid }}</p>
                        <div v-if="customer.email" class="flex gap-2 items-center text-[#344054] text-base font-normal mb-2">
                            <Icon name="mail01" size="20px" class="text-[#98A2B3]" />
                            {{ customer.email }}
                        </div>
                        <div v-if="customer.meta?.phone" class="flex gap-2 items-center text-[#344054] text-base font-normal mb-2">
                            <Icon name="phoneCall01" size="20px" class="text-[#98A2B3]" />
                            {{ customer.meta?.phone }}
                        </div>
                        <div v-if="customer.meta?.address" class="flex gap-2 items-center text-[#344054] text-base font-normal mb-2">
                            <Icon name="markerPin01" size="20px" class="text-[#98A2B3]" />
                            {{ customer.meta?.address }}
                        </div>
                    </div>

                    <!-- profile actions -->
                    <div class="flex items-end gap-3">
                        <Button size="md" type="secondary_gray" radius="square" classButton="px-[10px] h-fit" @click.prevent="deleteCustomer(customer)">
                            <Icon name="trash01" size="20px" />
                        </Button>
                        <Button size="md" type="primary" radius="square" classButton="h-fit" @click.prevent="router.push({ name: 'customer.edit', params: { customerId: customer.customer_id }, query: { backTo: route.fullPath } })">
                            Edit
                        </Button>
                    </div>
                </div>

                <!-- profile appointment info -->
                <div class="flex flex-col md:flex-row gap-5 rounded-xl border border-[#EAECF0] p-6 mb-7">
                    <div class="w-full py-3">
                        <p class="text-[#475467] text-sm font-semibold mb-3 capitalize">Total {{ $settings.labels.appointment?.singular }}</p>
                        <h1 class="text-[#101828] text-3xl font-semibold">{{ additionalInfos.totalAppointment }}</h1>
                    </div>
                    <div class="w-full py-3">
                        <p class="text-[#475467] text-sm font-semibold mb-3 capitalize">Canceled {{ $settings.labels.appointment?.singular }}</p>
                        <h1 class="text-[#101828] text-3xl font-semibold">{{ additionalInfos.canceled.length }}</h1>
                    </div>
                    <div class="w-full py-3">
                        <p class="text-[#475467] text-sm font-semibold mb-3">Total Spent</p>
                        <h1 class="text-[#101828] text-3xl font-semibold">{{ getPrice(additionalInfos.totalSpent) }}</h1>
                    </div>
                </div>


                <!-- profile appointment service info -->
                <div class="rounded-xl border border-[#EAECF0] p-6 mb-7">
                    <h2 class="text-[#101828] text-lg font-semibold mb-5 block">Appointed {{ String($settings.labels.service?.plural).toLowerCase() }}</h2>

                    <div class="grid grid-cols-2 md:flex md:flex-row gap-3">
                        <div v-for="(service, index) in additionalInfos.bookingServices" :key="index" class="flex w-full md:w-auto justify-center md:justify-normal items-center px-4 py-2.5 gap-2 rounded-[66px] border-[1.5px] border-[#EAECF0] max-w-[49%]">
                            <div :style="`background-color: ${service.color}`" class="w-[12px] h-[12px] rounded-full"></div>
                            <p v-if="service.service_name" class="text[#344054] text-sm font-semibold truncate" :title="service.service_name">{{ service.service_name }}</p>
                            <div class="rounded-[10px] bg-[#F2F4F7] text-[#344054] text-sm font-medium px-1.5">{{ service.totalBooked }}</div>
                        </div>
                        <div v-if="0 == additionalInfos.bookingServices.length" class="text-[red]">No data found</div>
                    </div>
                </div>


                <!-- appointment history -->
                <div class="rounded-xl border border-[#EAECF0] p-6 mb-7">
                    <h2 class="text-[#101828] text-lg font-semibold mb-5 block">{{ $settings.labels.appointment?.singular }} History</h2>

                    <Tabs default="upcoming" design="underline">
                        <Tab id="upcoming">Upcoming</Tab>
                        <Tab id="past">Past</Tab>
                        <Tab id="canceled">Canceled</Tab>

                        <template #upcoming>
                            <div class="flex flex-col gap-5 overflow-scroll md:overflow-visible">
                                <div class="flex items-center gap-5" v-for="(appointment, index) in additionalInfos.upcoming" :key="index">
                                    <!-- date -->
                                    <div class="py-[11px] px-[23.5px] text-center relative">
                                        <p class="text-[#101828] text-3xl font-semibold">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('DD') }}</p>
                                        <span class="text-[#98A2B3] text-sm font-normal">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('MMM') }}</span>
                                        <div class="absolute w-[1px] h-[52px] bg-[#EAECF0] top-[50%] right-0 -translate-y-1/2 -translate-x-1/2"></div>
                                    </div>

                                    <!-- appointment info -->
                                    <div :class="$settings.addons.staffs ? 'w-[341px]' : 'w-full'">
                                        <h2 class="text-[#101828] text-base font-semibold mb-1 truncate" :title="appointment.meta?.service_name">{{ appointment.meta?.service_name }}</h2>
                                        <p class="text-[#344054] text-sm font-medium">{{ moment(appointment.booking_time_start, 'hh:mm').format('hh:mm A') }}</p>
                                    </div>

                                    <!-- customer info -->
                                    <div class="flex gap-2 items-center w-[120px]" v-if="$settings.addons.staffs">
                                        <div class="w-[32px] h-[32px]">
                                            <img :src="$staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url ? $staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url : 'https://1.gravatar.com/avatar/729ae85bf62b9917e93538db2f2688ca?s=96&d=mm&r=g'" class=" rounded-full w-[32px] h-[32px]" />
                                            <!-- {{ appointment.meta?.staff_name?.split('')[0] }} -->
                                        </div>
                                        <p class="text-[#344054] text-sm font-medium truncate w-[80px]" :title="appointment.meta?.staff_name">{{ appointment.meta?.staff_name }}</p>
                                    </div>

                                    <!-- status -->
                                    <div class="px-2 py-0.5 rounded-2xl text-xs font-medium capitalize" :style="{ background: getStatusColors(appointment.meta?.status || '').bg, color: getStatusColors(appointment.meta?.status || '').color }">{{ appointment.meta?.status || '' }}</div>
                                </div>
                                <div v-if="0 == additionalInfos.upcoming.length" class="text-[red] text-center">No data found</div>

                            </div>
                        </template>

                        <template #past>
                            <div class="flex flex-col gap-5 overflow-scroll md:overflow-visible">
                                <div class="flex items-center gap-5" v-for="(appointment, index) in additionalInfos.past" :key="index">
                                    <!-- date -->
                                    <div class="py-[11px] px-[23.5px] text-center relative">
                                        <p class="text-[#101828] text-3xl font-semibold">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('DD') }}</p>
                                        <span class="text-[#98A2B3] text-sm font-normal">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('MMM') }}</span>
                                        <div class="absolute w-[1px] h-[52px] bg-[#EAECF0] top-[50%] right-0 -translate-y-1/2 -translate-x-1/2"></div>
                                    </div>

                                    <!-- appointment info -->
                                    <div :class="$settings.addons.staffs ? 'w-[341px]' : 'w-full'">
                                        <h2 class="text-[#101828] text-base font-semibold mb-1 truncate" :title="appointment.meta?.service_name">{{ appointment.meta?.service_name }}</h2>
                                        <p class="text-[#344054] text-sm font-medium">{{ moment(appointment.booking_time_start, 'hh:mm').format('hh:mm A') }}</p>
                                    </div>

                                    <!-- customer info -->
                                    <div class="flex gap-2 items-center w-[120px]" v-if="$settings.addons.staffs">
                                        <div class="w-[32px] h-[32px]">
                                            <img :src="$staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url ? $staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url : 'https://1.gravatar.com/avatar/729ae85bf62b9917e93538db2f2688ca?s=96&d=mm&r=g'" class=" rounded-full w-[32px] h-[32px]" />
                                            <!-- {{ appointment.meta?.staff_name?.split('')[0] }} -->
                                        </div>
                                        <p class="text-[#344054] text-sm font-medium truncate w-[80px]" :title="appointment.meta?.staff_name">{{ appointment.meta?.staff_name }}</p>
                                    </div>

                                    <!-- status -->
                                    <div class="px-2 py-0.5 rounded-2xl text-xs font-medium capitalize" :style="{ background: getStatusColors(appointment.meta?.status || '').bg, color: getStatusColors(appointment.meta?.status || '').color }">{{ appointment.meta?.status || '' }}</div>
                                </div>
                                <div v-if="0 == additionalInfos.past.length" class="text-[red] text-center">No data found</div>

                            </div>
                        </template>

                        <template #canceled>
                            <div class="flex flex-col gap-5 overflow-scroll md:overflow-visible">
                                <div class="flex items-center gap-5" v-for="(appointment, index) in additionalInfos.canceled" :key="index">
                                    <!-- date -->
                                    <div class="py-[11px] px-[23.5px] text-center relative">
                                        <p class="text-[#101828] text-3xl font-semibold">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('DD') }}</p>
                                        <span class="text-[#98A2B3] text-sm font-normal">{{ moment(appointment.booking_date, 'YYYY-MM-DD').format('MMM') }}</span>
                                        <div class="absolute w-[1px] h-[52px] bg-[#EAECF0] top-[50%] right-0 -translate-y-1/2 -translate-x-1/2"></div>
                                    </div>

                                    <!-- appointment info -->
                                    <div :class="$settings.addons.staffs ? 'w-[341px]' : 'w-full'">
                                        <h2 class="text-[#101828] text-base font-semibold mb-1 truncate" :title="appointment.meta?.service_name">{{ appointment.meta?.service_name }}</h2>
                                        <p class="text-[#344054] text-sm font-medium">{{ moment(appointment.booking_time_start, 'hh:mm').format('hh:mm A') }}</p>
                                    </div>

                                    <!-- customer info -->
                                    <div class="flex gap-2 items-center w-[120px]" v-if="$settings.addons.staffs">
                                        <div class="w-[32px] h-[32px]">
                                            <img :src="$staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url ? $staff.getStaffById(appointment.staff_id)?.meta?.profile_image_url : 'https://1.gravatar.com/avatar/729ae85bf62b9917e93538db2f2688ca?s=96&d=mm&r=g'" class=" rounded-full w-[32px] h-[32px]" />
                                            <!-- {{ appointment.meta?.staff_name?.split('')[0] }} -->
                                        </div>
                                        <p class="text-[#344054] text-sm font-medium truncate w-[80px]" :title="appointment.meta?.staff_name">{{ appointment.meta?.staff_name }}</p>
                                    </div>

                                    <!-- status -->
                                    <div class="px-2 py-0.5 rounded-2xl text-xs font-medium capitalize" :style="{ background: getStatusColors(appointment.meta?.status || '').bg, color: getStatusColors(appointment.meta?.status || '').color }">{{ appointment.meta?.status || '' }}</div>
                                </div>
                                <div v-if="0 == additionalInfos.canceled.length" class="text-[red] text-center">No data found</div>

                            </div>
                        </template>
                    </Tabs>
                </div>

                <!-- notes -->
                <div class="rounded-xl border border-[#EAECF0] p-6">
                    <div class="flex justify-between gap-2.5">
                        <h2 class="text-[#101828] text-lg font-semibold mb-5 block">Notes</h2>

                        <Button type="tertiary_color" radius="square" size="md" @click="showNote = true">
                            Add note
                            <template #before>
                                <Icon name="plusCircle" size="20px" />
                            </template>
                        </Button>
                    </div>

                    <div class="p-3 mb-2 rounded-lg shadow" v-if="showNote">
                        <form @submit.prevent="submitNote()">
                            <div class="w-full">
                                <Textarea v-model="noteText" placeholder="Write a note" class="py-1.5" textareaClass="h-[60px]" />
                            </div>

                            <div class="flex items-center justify-between gap-5">
                                <Button size="lg" type="secondary_color" radius="square" @click="showNote = false">Cancel</Button>
                                <Button submit size="lg" type="primary" radius="square" classButton="w-full"> Submit</Button>
                            </div>
                        </form>
                    </div>

                    <div class="flex flex-col gap-3" v-if="customer.notes">
                        <div v-for="note in customer.notes ">
                            <h2 class="mb-[7px] text-[#1D2939] text-base font-semibold" :title="formatCustomerNoteTime(note.added_at)">{{ formatCustomerNoteTime(note.added_at) }} by {{ note.admin_name }}</h2>
                            <p class="text-[#475467] text-base font-normal"> {{ note.text }}</p>
                        </div>
                        <div v-if="0 == customer.notes.length" class="text-[red] text-center">No data found</div>
                    </div>
                </div>
            </div>
        </template>
    </Modal>
</template>
